import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext.js';
import { api } from '../api/client.js';
import { copyToClipboard } from '../utils/clipboard.js';
import {
  Printer,
  Lock,
  User,
  ShieldCheck,
  AlertCircle,
  Smartphone,
  ShieldAlert,
  ArrowRight,
  RefreshCw,
  Copy,
  Check,
} from 'lucide-react';

export const LoginPage: React.FC<{ portal?: 'shop' | 'admin' | 'superadmin' }> = ({
  portal = 'shop',
}) => {
  const {
    login,
    verify2Fa,
    requires2Fa,
    deviceWaitingApproval,
    unapprovedDeviceId,
    approvalSentTo,
    ipBlockedMsg,
    refreshProfile,
  } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpToken, setTotpToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setLoading(true);
    try {
      if (requires2Fa) {
        await verify2Fa(email, totpToken);
      } else {
        await login(email, password, 'Web Dashboard', portal);
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const isSuper = portal === 'superadmin';
  const isAdmin = portal === 'admin';

  const theme = isSuper
    ? { from: '#7c3aed', to: '#4f46e5', glow: 'purple', badge: 'text-purple-300 bg-purple-500/10 border-purple-500/20', icon: <ShieldAlert className="w-6 h-6 text-white" />, label: 'Super Admin Root Governance', title: 'Super Admin Portal', accent: 'focus:border-purple-500 focus:ring-purple-500/20', btn: 'from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500', shadow: 'shadow-purple-600/25' }
    : isAdmin
    ? { from: '#4f46e5', to: '#2563eb', glow: 'indigo', badge: 'text-indigo-300 bg-indigo-500/10 border-indigo-500/20', icon: <ShieldCheck className="w-6 h-6 text-white" />, label: 'Admin Management Gateway', title: 'Admin Portal', accent: 'focus:border-indigo-500 focus:ring-indigo-500/20', btn: 'from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500', shadow: 'shadow-indigo-600/25' }
    : { from: '#10b981', to: '#0d9488', glow: 'emerald', badge: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/20', icon: <Printer className="w-6 h-6 text-white" />, label: 'Shopkeeper Counter Terminal', title: 'Shop Terminal', accent: 'focus:border-emerald-500 focus:ring-emerald-500/20', btn: 'from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400', shadow: 'shadow-emerald-600/25' };

  // ── IP Blocked Screen ──
  if (ipBlockedMsg) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg-primary)' }}>
        <div className="max-w-md w-full glass-card p-8 rounded-2xl border border-rose-500/20 text-center space-y-5 animate-scale-in">
          <div className="w-16 h-16 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-400 mx-auto flex items-center justify-center">
            <ShieldAlert className="w-8 h-8" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Access Restricted by IP Policy</h2>
            <p className="text-sm text-slate-400 leading-relaxed mt-2">{ipBlockedMsg}</p>
          </div>
          <div className="pt-3 border-t border-white/07">
            <p className="text-xs text-slate-500">
              Connect from an approved shop network or contact your administrator.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Device Pending Approval Screen ──
  const [pinCode, setPinCode] = useState('');
  const [verifyingPin, setVerifyingPin] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);

  if (deviceWaitingApproval) {
    const devId = localStorage.getItem('pb_browser_device_id') || 'dev_pending';
    const handleCopy = async () => {
      await copyToClipboard(devId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    };

    const handleVerifyPin = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!pinCode || pinCode.trim().length !== 6) return;
      setVerifyingPin(true);
      setPinError(null);
      try {
        await api.approveDevicePin(devId, pinCode.trim());
        alert('✅ Browser device successfully authorized! You can now log in.');
        window.location.reload();
      } catch (err: any) {
        setPinError(err.message || 'Invalid or expired 6-digit PIN');
      } finally {
        setVerifyingPin(false);
      }
    };

    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg-primary)' }}>
        <div className="max-w-md w-full glass-card p-8 rounded-2xl border border-amber-500/25 space-y-5 animate-scale-in">
          <div className="text-center space-y-3">
            <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/25 text-amber-400 mx-auto flex items-center justify-center">
              <Smartphone className="w-8 h-8 animate-pulse" />
            </div>
            <h2 className="text-xl font-bold text-white">Browser Authorization Required</h2>
            <p className="text-sm text-slate-300 leading-relaxed">
              A security authorization link and 6-digit PIN have been dispatched to your email:{' '}
              <strong className="text-emerald-400 font-mono text-sm block mt-1 break-all">
                {approvalSentTo || (typeof window !== 'undefined' ? localStorage.getItem('pb_approval_sent_to') : null) || 'your registered account email'}
              </strong>
            </p>
          </div>

          {/* Option A: Enter 6-digit PIN from email */}
          <form onSubmit={handleVerifyPin} className="p-4 rounded-xl bg-black/40 border border-emerald-500/20 space-y-3">
            <div className="text-[11px] font-semibold text-emerald-400 uppercase tracking-wider">
              Authorize Instantly with 6-Digit Email PIN
            </div>
            <input
              type="text"
              maxLength={6}
              value={pinCode}
              onChange={(e) => setPinCode(e.target.value.replace(/\D/g, ''))}
              placeholder="Enter 6-digit PIN"
              className="w-full text-center text-xl font-mono tracking-widest py-2.5 px-3 rounded-lg bg-black/50 border border-white/10 text-white focus:outline-none focus:border-emerald-500"
            />
            {pinError && (
              <p className="text-xs text-rose-400 font-medium">{pinError}</p>
            )}
            <button
              type="submit"
              disabled={verifyingPin || pinCode.length !== 6}
              className="w-full py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-black font-bold text-xs transition flex items-center justify-center gap-1.5"
            >
              {verifyingPin ? 'Verifying PIN...' : 'Authorize Browser with PIN'}
            </button>
          </form>

          {/* Option B: Click link in email or check status */}
          <div className="space-y-2">
            <p className="text-xs text-slate-400 text-center">
              Or click the <strong>"ACCEPT & APPROVE"</strong> link directly in your email.
            </p>
            <button
              onClick={() => refreshProfile()}
              className="w-full py-2.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 text-xs font-semibold flex items-center justify-center gap-2 transition"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Check Approval Status
            </button>
          </div>

          <div className="pt-2 border-t border-white/05 flex items-center justify-between text-[11px] text-slate-500">
            <span>Device ID: {devId.slice(0, 10)}...</span>
            <button onClick={handleCopy} className="text-amber-400 hover:underline">
              {copied ? 'Copied' : 'Copy ID'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Normal Login Form ──
  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" style={{ background: 'var(--bg-primary)' }}>
      {/* Ambient glow orbs */}
      <div
        className="absolute top-1/4 left-1/3 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, ${isSuper ? 'rgba(124,58,237,0.12)' : isAdmin ? 'rgba(79,70,229,0.10)' : 'rgba(16,185,129,0.10)'} 0%, transparent 70%)`,
          filter: 'blur(40px)',
        }}
      />
      <div
        className="absolute bottom-1/4 right-1/3 w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, ${isSuper ? 'rgba(99,102,241,0.08)' : isAdmin ? 'rgba(37,99,235,0.08)' : 'rgba(20,184,166,0.08)'} 0%, transparent 70%)`,
          filter: 'blur(40px)',
        }}
      />

      <div className="max-w-[420px] w-full glass-card rounded-2xl p-8 relative z-10 animate-scale-in">
        {/* Header */}
        <div className="text-center mb-8">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl"
            style={{ background: `linear-gradient(135deg, ${theme.from}, ${theme.to})` }}
          >
            {theme.icon}
          </div>

          <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-mono font-bold uppercase tracking-widest border mb-3 ${theme.badge}`}>
            {theme.label}
          </span>

          <h1 className="text-2xl font-bold text-white tracking-tight">{theme.title}</h1>
          <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">
            {requires2Fa
              ? 'Enter the 6-digit authentication code from your app'
              : isSuper
              ? 'Root infrastructure access — super admin credentials set at server boot'
              : isAdmin
              ? 'Enter the admin credentials created by Super Admin'
              : 'Enter the shop credentials created by your Admin'}
          </p>
        </div>

        {/* Error Banner */}
        {errorMessage && (
          <div className="mb-5 p-3.5 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-start gap-3 text-rose-400 text-sm animate-fade-up">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span className="text-[13px]">{errorMessage}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {!requires2Fa ? (
            <>
              <div>
                <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
                  {isSuper ? 'Superadmin Username' : isAdmin ? 'Admin Username' : 'Shopkeeper Username'}
                </label>
                <div className="relative">
                  <User className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    id="username-input"
                    type="text"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={isSuper ? 'superadmin' : isAdmin ? 'e.g. admin' : 'e.g. shop1, campus_print'}
                    className="input-field input-field-icon"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    id="password-input"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••••"
                    className="input-field input-field-icon"
                  />
                </div>
              </div>
            </>
          ) : (
            <div>
              <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Two-Factor Authentication Code
              </label>
              <div className="relative">
                <ShieldCheck className="w-4 h-4 text-emerald-500 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  id="totp-input"
                  type="text"
                  required
                  autoFocus
                  maxLength={8}
                  value={totpToken}
                  onChange={(e) => setTotpToken(e.target.value.trim())}
                  placeholder="123456"
                  className="input-field input-field-icon text-center font-mono tracking-[0.4em] text-lg"
                />
              </div>
            </div>
          )}

          <button
            id="login-submit-btn"
            type="submit"
            disabled={loading}
            className={`w-full py-3 px-4 rounded-xl text-white font-semibold text-sm shadow-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50 cursor-pointer bg-gradient-to-r ${theme.btn} shadow-lg ${theme.shadow} mt-2`}
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <span>{requires2Fa ? 'Verify Code' : 'Sign In'}</span>
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>

        {/* Security Notice (Shopkeeper) */}
        {!isAdmin && !isSuper && (
          <div className="mt-6 p-3.5 rounded-xl bg-emerald-950/30 border border-emerald-500/15 space-y-1.5">
            <div className="flex items-center gap-1.5 text-emerald-400 font-semibold text-xs">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>Hardware Lock Security Active</span>
            </div>
            <p className="text-[11px] text-slate-400 leading-relaxed">
              Only Admin-approved browser devices can log in — even on shared networks.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
